import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';
import { TabPanel, TabView } from 'primereact/tabview';
import { flagCheckHistoryApi, flagCheckInApi, flagLookupsApi, memberFlagsApi } from '../../api/flagSystemApi';
import FlagCheckInDialog from './FlagCheckInDialog';
import FlagMemberMembershipNumberDialog from './FlagMemberMembershipNumberDialog';
import {
  flagError, flagFieldErrors, formatFlagInstant, formatLegacyScan,
  memberFlagView, validateMemberChange,
} from '../../utils/flagSystem';
import { Feedback, Field, FlagPage, ResourceState, useRemoteResource } from './FlagSystemCommon';
import { flagApplicabilityLabel, flagApplicabilityValue, hasFlagApplicability } from '../../utils/flagApplicability';

const MemberAssignments = ({ account, scope, locationName, memberId, catalog, onAccessError, onMemberRenamed }) => {
  const loader = useCallback(() => memberFlagsApi.list(scope, memberId), [scope, memberId]);
  const resource = useRemoteResource(loader, onAccessError);
  const [dialog, setDialog] = useState(null);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [mustReload, setMustReload] = useState(false);
  const assignments = resource.data || [];
  const options = (catalog.data || []).filter(flag => flag.active !== false)
    .map(flag => memberFlagView(flag, account))
    .filter(flag => flag.canAddRemove && !assignments.some(assignment => assignment.flag.flagId === flag.flagId));
  const canEditMembershipNumber = account.isCustomer || (!resource.loading && !resource.error &&
    assignments.every(assignment => memberFlagView(assignment.flag, account).canAddRemove));
  const dialogFlag = dialog && dialog.assignment ? memberFlagView(dialog.assignment.flag, account) :
    options.find(flag => flag.flagId === selectedFlag);
  const open = (assignment) => { setDialog(assignment ? { assignment } : {}); setComment(''); setErrors({}); setError(''); setMessage(''); };
  const close = () => { if (!saving) { setDialog(null); setComment(''); setErrors({}); setError(''); } };
  const reload = () => { setMustReload(false); setError(''); resource.reload(); catalog.reload(); };
  const submit = async () => {
    const validation = validateMemberChange(memberId, comment, account.isCustomer);
    if (!dialog.assignment && !options.some(flag => flag.flagId === selectedFlag)) validation.flagId = 'Select an available flag.';
    setErrors(validation);
    if (Object.keys(validation).length || mustReload) return;
    setSaving(true); setError('');
    try {
      if (dialog.assignment) await memberFlagsApi.remove(scope, dialog.assignment.memberFlagId, comment);
      else await memberFlagsApi.assign(scope, { memberId, flagId: selectedFlag, comment });
      setMessage(dialog.assignment ? 'Flag removed. Its assignment history is retained by the server.' : 'Flag assigned.');
      setDialog(null); setComment(''); setSelectedFlag(null); resource.reload(); catalog.reload();
    } catch (failure) {
      onAccessError(failure); setError(flagError(failure, 'Unable to change the member assignment.'));
      setErrors(flagFieldErrors(failure));
      if (!failure.response || failure.response.status === 409) setMustReload(true);
    } finally { setSaving(false); }
  };
  return <div className="flag-member-card">
    <div className="flex align-items-center justify-content-between gap-3 mb-3">
      <h5 className="mb-0">Member ID: {memberId}</h5>
      <div className="flex gap-2">
        {canEditMembershipNumber && <FlagMemberMembershipNumberDialog account={account} scope={scope} memberId={memberId}
          onAccessError={onAccessError} onRefresh={reload} onRenamed={(nextMemberId) => {
            onMemberRenamed(nextMemberId);
          }} />}
        <FlagCheckInDialog scope={scope} locationName={locationName} memberId={memberId} onAccessError={onAccessError} />
        <Button label="Reload member flags" icon="pi pi-refresh" className="p-button-text" disabled={saving} onClick={reload} />
      </div>
    </div>
    <Feedback error={!dialog ? error : ''} message={message} />
    <p className="flag-note">This is an exact identifier lookup, not membership verification or an access decision.</p>
    <ResourceState resource={resource}>
      {!assignments.length && <Message severity="info" text="No current flags found for this Member ID. This may mean no flag-system record, no active assignments, or disabled definitions; it does not establish membership or access eligibility." />}
      {assignments.map(assignment => {
        const flag = memberFlagView(assignment.flag, account);
        const actionText = flag.action || (!account.isCustomer && account.role !== 'SeniorLocalUser' ? 'No action configured for your role' : null);
        return <div className="flag-member-card" key={assignment.memberFlagId}>
          <div className="flex align-items-center justify-content-between flex-wrap gap-3">
            {flag.name && <strong>{flag.name}</strong>}
            {hasFlagApplicability(assignment.flag) && <Tag value={flagApplicabilityLabel(assignment.flag)}
              severity={flagApplicabilityValue(assignment.flag) ? 'info' : 'warning'} />}
            <div className="flex gap-2">
              {flag.canAddRemove && <Button label="Remove Flag" icon="pi pi-minus" className="p-button-danger p-button-text"
                disabled={saving || mustReload} onClick={() => open(assignment)} />}
              {flag.canOverride && <Button label="Override unavailable" icon="pi pi-lock" className="p-button-warning" disabled
                aria-describedby="override-dependency" />}
            </div>
          </div>
          {flag.description && <p>{flag.description}</p>}
          {actionText && <p>{actionText}</p>}
          {account.isCustomer && <div className="flag-note">
            {[['juniorAction', 'Junior Local User'], ['dutyManagerAction', 'Duty Manager'], ['seniorAction', 'Senior Local User']]
              .map(([field, label]) => assignment.flag[field] && <p key={field}><strong>{label}:</strong> {assignment.flag[field]}</p>)}
          </div>}
          <small className="text-color-secondary">Assigned {formatFlagInstant(assignment.addedAt)} (British time)</small>
        </div>;
      })}
    </ResourceState>
    <ResourceState resource={catalog}>
      <div className="flag-member-search mt-3">
        <Field id="member-flag-choice" label="Flag to assign">
          <Dropdown inputId="member-flag-choice" options={options} optionLabel="displayLabel" optionValue="flagId"
            value={options.some(flag => flag.flagId === selectedFlag) ? selectedFlag : null}
            onChange={event => setSelectedFlag(event.value)} placeholder="Select an available flag"
            disabled={saving || resource.loading || Boolean(resource.error) || mustReload || !options.length} />
        </Field>
        <Button label="Assign Flag" icon="pi pi-plus" className="p-button-success" onClick={() => open(null)}
          disabled={saving || resource.loading || Boolean(resource.error) || mustReload || !options.some(flag => flag.flagId === selectedFlag)} />
      </div>
      {!options.length && <p className="flag-note">No unassigned flags are available for this account. Only active flags you may add/remove are offered.</p>}
    </ResourceState>
    {account.isCustomer && <p className="flag-note">Review recorded flag-assignment activity in the{' '}
      <Link to={`/support/flags/audit-log?memberId=${encodeURIComponent(memberId)}`}>Audit Log</Link>.</p>}
    <Dialog header={dialog && dialog.assignment ? 'Remove Flag' : 'Assign Flag'} visible={Boolean(dialog)}
      className="flag-editor" modal closable={!saving} onHide={close}
      footer={<><Button label="Cancel" className="p-button-text" disabled={saving} onClick={close} />
        <Button label="Confirm" icon="pi pi-check" loading={saving}
          disabled={saving || mustReload || resource.loading || catalog.loading || Boolean(resource.error) || Boolean(catalog.error)} onClick={submit} /></>}>
      <Feedback error={error} />
      {mustReload && <><Message severity="warn" text="Reload current assignments before repeating this request. A network failure may have occurred after the server completed the change." />
        <Button label="Reload current data" className="p-button-text" onClick={reload} disabled={saving} /></>}
      {errors.flagId && <Message severity="error" text={errors.flagId} />}
      <p>Member ID: <strong>{memberId}</strong></p>
      {dialogFlag && dialogFlag.displayLabel && <p>{dialogFlag.displayLabel}</p>}
      <Field id="member-flag-comment" label={`Comment${account.isCustomer ? ' (optional)' : ' (required)'}`} error={errors.comment}>
        <InputTextarea id="member-flag-comment" value={comment} maxLength={1000} rows={3} disabled={saving}
          onChange={event => setComment(event.target.value)} />
      </Field>
    </Dialog>
  </div>;
};

const MemberSearch = ({ account, scope, locationName, onAccessError }) => {
  const [input, setInput] = useState('');
  const [memberId, setMemberId] = useState(null);
  const [lookupVersion, setLookupVersion] = useState(0);
  const [error, setError] = useState('');
  const [renameMessage, setRenameMessage] = useState('');
  const loader = useCallback(() => memberFlagsApi.catalog(scope), [scope]);
  const catalog = useRemoteResource(loader, onAccessError);
  const search = (event) => {
    event.preventDefault();
    const validation = validateMemberChange(input, '', true);
    if (validation.memberId) { setError(validation.memberId); setMemberId(null); return; }
    setError(''); setRenameMessage(''); setMemberId(input.trim()); setLookupVersion(value => value + 1);
  };
  return <>
    <form className="flag-member-search" onSubmit={search}>
      <Field id="flag-member-id" label="Member ID" error={error}>
        <InputText id="flag-member-id" value={input} maxLength={200} placeholder="Enter exact Member ID"
          onChange={event => { setInput(event.target.value); setMemberId(null); setError(''); }} />
      </Field>
      <Button type="submit" label="Find Member Flags" icon="pi pi-search" />
    </form>
    <p className="flag-note">Member IDs retain case and leading zeros. The first successful flag assignment registers an identifier-only flag-system record; there is no standalone member-create endpoint here.</p>
    {renameMessage && <Message severity="success" text={renameMessage} />}
    {catalog.error && <ResourceState resource={catalog} />}
    {catalog.data && !catalog.data.some(flag => flag.active !== false) && <Message severity="info" text="No flags are available. Create active flags in Flag Management first." />}
    {memberId && <MemberAssignments key={`${memberId}:${lookupVersion}`} account={account} scope={scope} locationName={locationName} memberId={memberId}
      catalog={catalog} onAccessError={onAccessError} onMemberRenamed={nextMemberId => {
        setInput(nextMemberId); setMemberId(nextMemberId); setRenameMessage('FLAG-USER number updated. Existing flags remain attached; membership accounts and LMS were not changed.');
        setLookupVersion(value => value + 1); catalog.reload();
      }} />}
  </>;
};

const ScanBatches = ({ account, scope, accessPointIds, allowed, onAccessError }) => {
  const [page, setPage] = useState(0);
  const [records, setRecords] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 100;
  const loader = useCallback(async () => {
    const batches = await Promise.all(accessPointIds.map(accessPointId => flagCheckHistoryApi.list(scope, accessPointId, page, pageSize)));
    return { page, records: [].concat(...batches.map(batch => batch.items)), totalCount: batches.reduce((total, batch) => total + batch.totalCount, 0) };
  }, [scope, accessPointIds, page]);
  const resource = useRemoteResource(loader, onAccessError);
  useEffect(() => {
    if (!resource.data) return;
    const batch = resource.data;
    setRecords(previous => {
      if (batch.page === 0) return batch.records;
      const combined = new Map(previous.map(record => [`${record.historyTileId}:${record.id}`, record]));
      batch.records.forEach(record => combined.set(`${record.historyTileId}:${record.id}`, record));
      return Array.from(combined.values());
    });
    setHasMore(batch.records.length < batch.totalCount);
  }, [resource.data]);
  const filtered = records.filter(record => allowed ? record.status === 'FlagsPassed' : record.status === 'FlagsBlocked');
  const siteInstructions = record => {
    if (record.historicalDetailsAvailable === false) return 'Historical instructions unavailable';
    const actions = Array.isArray(record.actions) ? record.actions.filter(action => typeof action === 'string' && action.trim()) : [];
    if (actions.length) return <div>{actions.map((action, index) => <div key={`${record.id}:${index}`}>{action}</div>)}</div>;
    return record.status === 'FlagsBlocked' ? 'No instruction was configured for your role at the time' : null;
  };
  const customerTriggeredFlags = record => !record.historicalDetailsAvailable ? 'Historical flag instructions unavailable' :
    (record.triggeredFlags || []).map(flag => <div key={`${flag.flagId}:${flag.memberFlagId}`}>
      <strong>{flag.code || `Flag ${flag.flagId}`}</strong>{flag.name ? ` — ${flag.name}` : ''}: {flag.overridden ? 'Allowed by existing override' : flag.reason}
      {!flag.overridden && (flag.actions ? <div>{[['juniorAction', 'Junior Local User'], ['dutyManagerAction', 'Duty Manager'], ['seniorAction', 'Senior Local User']].map(([key, label]) => flag.actions[key] && <div key={key}>{label}: {flag.actions[key]}</div>)}</div> :
        <div>{flag.action && flag.action.trim() ? flag.action : 'No instruction configured at the time'}</div>)}
    </div>) || (record.flagsAllowed === true ? 'No flag blocked that check.' : '');
  return <>
    <p className="flag-note">{filtered.length} {allowed ? 'flags passed' : 'blocked by flags'} records in {records.length} loaded flag checks. Times shown in Europe/London.</p>
    {resource.loading && <Message severity="info" text="Loading flag check history..." />}
    {resource.error && <div className="flex align-items-center gap-2 mb-3"><Message severity="error" text={resource.error} />
      <Button label="Retry batch" onClick={resource.reload} /></div>}
    <DataTable value={filtered} dataKey="id" emptyMessage={resource.loading ? 'Loading...' : 'No matching flag checks.'} responsiveLayout="scroll">
      <Column header="Evaluation time" body={record => formatFlagInstant(record.checkedAtUtc)} />
      <Column field="memberId" header="Member number" />
      <Column header="Access point" body={record => [record.gate, record.tile, record.direction].filter(Boolean).join(' — ') || record.accessPointId} />
      <Column header="Outcome" body={record => <Tag value={record.status === 'FlagsBlocked' ? 'Blocked by flags' :
        record.status === 'FlagsPassed' ? 'Flags passed' : 'Historical evaluation details unavailable'} severity={record.status === 'FlagsBlocked' ? 'danger' : 'info'} />} />
      <Column header={account.isCustomer ? 'Triggered flags' : 'Instructions'}
        body={record => account.isCustomer ? customerTriggeredFlags(record) : siteInstructions(record)} />
    </DataTable>
    {hasMore && <Button label="Load more historical records" icon="pi pi-chevron-down" className="mt-3"
      disabled={resource.loading || Boolean(resource.error)} onClick={() => setPage(value => value + 1)} />}
  </>;
};

const HistoricalScans = ({ account, scope, allowed, onAccessError }) => {
  const loader = useCallback(() => flagCheckInApi.accessPoints(scope), [scope]);
  const points = useRemoteResource(loader, onAccessError);
  const [selectedAccessPointIds, setSelectedAccessPointIds] = useState([]);
  const [version, setVersion] = useState(0);
  const options = (points.data || []).filter(point => point.direction).map(point => ({ ...point, label: [point.gate, point.tile, point.direction].filter(Boolean).join(' — ') || `Access point ${point.accessPointId}` }));
  const accessPointIds = selectedAccessPointIds.filter(id => options.some(point => point.accessPointId === id));
  return <>
    <ResourceState resource={points}>
      <Field id="history-tile" label="Access points">
        <MultiSelect inputId="history-tile" value={accessPointIds} options={options} optionLabel="label" optionValue="accessPointId"
          placeholder="Select one or more access points" display="chip" onChange={event => setSelectedAccessPointIds(event.value || [])} />
      </Field>
      {options.length > 0 && <div className="flex gap-2 mb-3">
        <Button label="Select all access points" className="p-button-text p-button-sm" onClick={() => setSelectedAccessPointIds(options.map(point => point.accessPointId))}
          disabled={accessPointIds.length === options.length} />
        <Button label="Clear access points" className="p-button-text p-button-sm" onClick={() => setSelectedAccessPointIds([])} disabled={!accessPointIds.length} />
      </div>}
      {points.data && !options.length && <p>No configured access points are available for this location.</p>}
    </ResourceState>
    {accessPointIds.length > 0 && <>
      <Button label="Refresh flag check history" icon="pi pi-refresh" className="mb-3" onClick={() => setVersion(value => value + 1)} />
      <ScanBatches key={`${accessPointIds.join(':')}:${version}`} account={account} scope={scope} accessPointIds={accessPointIds}
        allowed={allowed} onAccessError={onAccessError} />
    </>}
  </>;
};

const ScopedSiteViewer = ({ account, locationId, locationName, onAccessError }) => {
  const [activeTab, setActiveTab] = useState(0);
  const scope = React.useMemo(() => ({ customerId: account.customerId, locationId }), [account.customerId, locationId]);
  return <>
    <TabView activeIndex={activeTab} onTabChange={event => setActiveTab(event.index)}>
      <TabPanel header="Member Search" />
      <TabPanel header="Allowed Scans" />
      <TabPanel header="Denied Scans" />
    </TabView>
    <div hidden={activeTab !== 0}><MemberSearch account={account} scope={scope} locationName={locationName} onAccessError={onAccessError} /></div>
    <div hidden={activeTab === 0}><HistoricalScans account={account} scope={scope} allowed={activeTab === 1} onAccessError={onAccessError} /></div>
  </>;
};
const SiteViewer = ({ account, onAccessError }) => {
  const loader = useCallback(() => account.isCustomer ? flagLookupsApi.locations(account.customerId) : Promise.resolve([]), [account.customerId, account.isCustomer]);
  const resource = useRemoteResource(loader, onAccessError);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const locations = resource.data || [];
  const locationId = account.isCustomer ? (locations.length === 1 ? locations[0].id :
    (locations.some(location => location.id === selectedLocation) ? selectedLocation : null)) :
    (account.assignedLocationIds.includes(account.locationId) ? account.locationId :
      (account.assignedLocationIds.includes(selectedLocation) ? selectedLocation : null));
  const locationName = account.isCustomer ? (locations.find(location => location.id === locationId) || {}).name : account.locationName;
  const assignedLocationOptions = account.isCustomer ? [] : account.assignedLocationIds.map(id => ({
    id, name: account.locationName && id === account.locationId ? account.locationName : `Location ${id}`,
  }));
  return <FlagPage title="Site Viewer">
    <ResourceState resource={resource}>
      {account.isCustomer ? <><Field id="flag-location" label="Location">
        <Dropdown inputId="flag-location" value={locationId} options={locations} optionLabel="name" optionValue="id"
          placeholder="Select one of your customer locations" onChange={event => setSelectedLocation(event.value)} />
      </Field>
      {!locations.length && <p>No locations are available for your customer.</p>}</> :
        account.assignedLocationIds.length === 1 ? null :
          <><Field id="flag-site-location" label="Assigned site">
            <Dropdown inputId="flag-site-location" value={locationId} options={assignedLocationOptions} optionLabel="name" optionValue="id"
              placeholder="Select one of your assigned sites" onChange={event => setSelectedLocation(event.value)} />
          </Field>
          {!locationId && <Message severity="info" text="Select one of your assigned sites to view site data. Location names are unavailable from the current profile response." />}</>}
      {locationId && <ScopedSiteViewer key={`${account.sessionKey}:${account.role}:${account.customerId}:${locationId}`} account={account} locationId={locationId} locationName={locationName} onAccessError={onAccessError} />}
    </ResourceState>
  </FlagPage>;
};
export default SiteViewer;
